import { MapPin, Mail, Phone, Clock, ArrowUpRight, Facebook, Instagram, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";

const ADDRESS = "Via Sacro Cuore 2, 45100 Rovigo (RO)";
const PHONE_DISPLAY = "392 816 0004";
const PHONE_TEL = "+393928160004";
const WHATSAPP = "https://wa.me/393928160004";
const MAPS_EMBED = "https://www.google.com/maps?q=Via%20Sacro%20Cuore%202%2C%2045100%20Rovigo%20RO&output=embed";
const MAPS_DIR = "https://www.google.com/maps/dir/?api=1&destination=Via%20Sacro%20Cuore%202%2C%2045100%20Rovigo%20RO";

const HOURS = [
  { d: "Lunedì", h: "16:00 – 20:00" },
  { d: "Martedì – Sabato", h: "9:00 – 12:30 · 16:00 – 20:00" },
  { d: "Domenica", h: "Chiuso" },
];

export const LocationSection = () => {
  return (
    <section id="dove-siamo" className="bg-bone py-24 lg:py-32" data-testid="location-section" aria-labelledby="dove-siamo-title">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mb-14"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-claret mb-5">Dove siamo</p>
          <h2 id="dove-siamo-title" className="font-serif-display text-5xl lg:text-6xl leading-[1.05]">
            L'Enoteca Uvaspina,<br /><span className="italic">nel cuore di Rovigo.</span>
          </h2>
          <p className="text-ink-soft leading-relaxed mt-6 max-w-xl">
            UvaShop è lo shop online dell'Enoteca Uvaspina. Vieni a trovarci in negozio a Rovigo per scoprire la nostra selezione, oppure ordina online con spedizione in tutta Italia, consegna a domicilio in zona o ritiro in sede.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
          {/* INFO */}
          <div className="flex flex-col justify-between">
            <address className="not-italic space-y-8">
              <div className="flex items-start gap-4">
                <MapPin size={22} strokeWidth={1.5} className="text-claret shrink-0 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-ink-soft mb-1">Indirizzo</p>
                  <p className="text-lg" data-testid="location-address">{ADDRESS}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Clock size={22} strokeWidth={1.5} className="text-claret shrink-0 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-ink-soft mb-2">Orari di apertura</p>
                  <ul className="space-y-1" data-testid="location-hours">
                    {HOURS.map((r) => (
                      <li key={r.d} className="flex flex-col sm:flex-row sm:gap-3">
                        <span className="text-ink w-48">{r.d}</span>
                        <span className="text-ink-soft">{r.h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Phone size={22} strokeWidth={1.5} className="text-claret shrink-0 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-ink-soft mb-1">Telefono</p>
                  <a href={`tel:${PHONE_TEL}`} data-testid="location-phone" className="text-lg link-underline hover:text-claret transition-colors">{PHONE_DISPLAY}</a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Mail size={22} strokeWidth={1.5} className="text-claret shrink-0 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-ink-soft mb-1">Email</p>
                  <a href="mailto:uvaspinaenoteca@gmail.com" data-testid="location-email" className="text-lg link-underline hover:text-claret transition-colors">uvaspinaenoteca@gmail.com</a>
                </div>
              </div>
            </address>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={MAPS_DIR} target="_blank" rel="noopener noreferrer"
                data-testid="location-directions"
                className="inline-flex items-center gap-2 bg-ink hover:bg-claret text-bone px-7 py-4 uppercase tracking-[0.12em] text-sm transition-colors"
              >
                Indicazioni stradali <ArrowUpRight size={18} />
              </a>
              <a
                href={WHATSAPP} target="_blank" rel="noopener noreferrer"
                data-testid="location-whatsapp"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-white px-7 py-4 uppercase tracking-[0.12em] text-sm transition-colors"
              >
                <MessageCircle size={18} /> WhatsApp
              </a>
              <div className="flex items-center gap-3">
                <a href="https://www.facebook.com/uvaspinaenoteca/?locale=it_IT" target="_blank" rel="noopener noreferrer" aria-label="Facebook Enoteca Uvaspina" data-testid="location-facebook" className="w-12 h-12 border border-ink/20 hover:border-claret hover:text-claret flex items-center justify-center transition-colors">
                  <Facebook size={20} strokeWidth={1.5} />
                </a>
                <a href="https://www.instagram.com/uvaspinaenoteca" target="_blank" rel="noopener noreferrer" aria-label="Instagram Enoteca Uvaspina" data-testid="location-instagram" className="w-12 h-12 border border-ink/20 hover:border-claret hover:text-claret flex items-center justify-center transition-colors">
                  <Instagram size={20} strokeWidth={1.5} />
                </a>
              </div>
            </div>
          </div>

          {/* MAP */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative min-h-[360px] lg:min-h-full overflow-hidden border border-ink/10 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.4)]"
          >
            <iframe
              title="Mappa Enoteca Uvaspina a Rovigo"
              src={MAPS_EMBED}
              className="absolute inset-0 w-full h-full grayscale-[0.15]"
              style={{ border: 0, minHeight: 360 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
